import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { InboxArrowDownIcon } from '@heroicons/react/24/outline';
import { useDropzone } from 'react-dropzone';
import { API_URL } from '@/lib/api';

interface RequestInfo {
  title: string;
  ownerName?: string | null;
}

/**
 * The public face of a file request: no account, no navigation — the receiver opens the
 * link, drops their file, done. The vault validates the request server-side (active,
 * not expired, quota) and lands the upload in the owner's chosen folder.
 */
export function RequestLandingPage() {
  const { shortId } = useParams<{ shortId: string }>();
  const { t } = useTranslation();
  const [info, setInfo] = useState<RequestInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [uploaded, setUploaded] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/requests/public/${encodeURIComponent(shortId ?? '')}`);
        const data = (await res.json().catch(() => ({}))) as RequestInfo & { error?: string };
        if (!res.ok) throw new Error(data.error || `Request failed with ${res.status}`);
        setInfo(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      }
    })();
  }, [shortId]);

  const upload = useCallback(
    async (files: File[]) => {
      if (!files.length) return;
      setUploading(true);
      try {
        for (const file of files) {
          const form = new FormData();
          form.append('file', file);
          const res = await fetch(`${API_URL}/api/requests/public/${encodeURIComponent(shortId ?? '')}/upload`, {
            method: 'POST',
            body: form,
          });
          if (!res.ok) {
            const data = (await res.json().catch(() => ({}))) as { error?: string };
            throw new Error(data.error || `Upload failed with ${res.status}`);
          }
          setUploaded(file.name);
        }
        toast.success(t('request.uploaded'));
      } catch (err) {
        toast.error(err instanceof Error ? err.message : String(err));
      } finally {
        setUploading(false);
      }
    },
    [shortId, t],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop: (accepted) => void upload(accepted) });

  return (
    <div className="flex min-h-screen w-full flex-col items-center justify-center gap-6 bg-background p-6">
      <div className="flex w-full max-w-md flex-col items-center gap-4 rounded-xl border border-border-color bg-card p-8 shadow-sm">
        <InboxArrowDownIcon className="h-10 w-10 text-accent" />
        <h1 className="text-center text-xl font-semibold text-foreground">
          {info?.title ?? t('request.title')}
        </h1>
        {info?.ownerName && <p className="text-sm text-muted-foreground">{t('request.from', { name: info.ownerName })}</p>}

        {error ? (
          <p className="text-center text-sm text-destructive">{error}</p>
        ) : uploaded ? (
          <p className="text-center text-sm text-success">{t('request.done', { name: uploaded })}</p>
        ) : (
          <div
            {...getRootProps()}
            className={`w-full cursor-pointer rounded-lg border-2 border-dashed px-6 py-10 text-center transition-colors ${
              isDragActive ? 'border-accent bg-active' : 'border-border-color'
            }`}
          >
            <input {...getInputProps()} />
            <p className="text-sm text-muted-foreground">
              {uploading ? t('request.uploading') : t('request.dropHere')}
            </p>
          </div>
        )}
      </div>
      <p className="text-xs text-muted-foreground">{t('request.securedBy')}</p>
    </div>
  );
}
